import React, { useState } from "react";
import api from "@/lib/api";
import { ShieldAlert, Search, ExternalLink, X, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * NswAgentCheck, public lookup against the NSW Fair Trading
 * "Name and Shame Register" (real estate / strata / stock & station agents).
 *
 * Renders as a compact button inline in the CaseHeader for NSW cases.
 * Clicking opens a modal with a search box (prefilled with the agency /
 * agent name from the case) and shows any matching disciplinary actions.
 *
 * Endpoint: GET /api/nsw/agent-check?q=...  (public, rate-limited by IP).
 * Data source: nsw.gov.au, we link out to the source on every result.
 */
export default function NswAgentCheck({ initialQuery = "", triggerless = false, open: openProp, onClose }) {
  const isControlled = triggerless || openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? !!openProp : internalOpen;
  const closeModal = () => { if (onClose) onClose(); if (!isControlled) setInternalOpen(false); };
  const [q, setQ] = useState(initialQuery || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async (queryStr) => {
    const query = (queryStr ?? q).trim();
    if (query.length < 2) {
      setErr("Type the agency / agent name (or licence number), at least 2 characters.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get(`/nsw/agent-check`, { params: { q: query } });
      setResult(data);
      if (data.error) setErr(data.error);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't reach the register right now.");
    } finally {
      setBusy(false);
    }
  };

  const openAndPrefill = () => {
    if (!isControlled) setInternalOpen(true);
    if (initialQuery && !result) {
      // Auto-search on first open if we have a name to check
      setTimeout(() => run(initialQuery), 50);
    }
  };

  // When the controlled `open` flips on (or initialQuery changes while open),
  // auto-run the search so consumers can just toggle a flag.
  React.useEffect(() => {
    if (isControlled && openProp && initialQuery && (!result || result.query?.toLowerCase() !== initialQuery.toLowerCase())) {
      setQ(initialQuery);
      run(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProp, initialQuery, isControlled]);

  return (
    <>
      {!triggerless && (
        <button
          onClick={openAndPrefill}
          className="inline-flex items-center gap-1.5 text-[12px] text-[#1B6C62] hover:underline"
          data-testid="nsw-agent-check-open"
          title="Search the NSW Fair Trading Name & Shame Register"
        >
          <ShieldAlert size={12} strokeWidth={1.5} />
          Check NSW Name &amp; Shame
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={closeModal}
          data-testid="nsw-agent-check-modal"
        >
          <div
            className="bg-[#FAF9F6] border border-[#E5E4DE] w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#E5E4DE] flex items-start justify-between gap-3">
              <div>
                <div className="label-mono text-[#8A6D3B] mb-0.5 flex items-center gap-1.5">
                  <ShieldAlert size={11} strokeWidth={1.5} /> NSW Fair Trading · Name &amp; Shame Register
                </div>
                <h3 className="font-serif text-xl text-[#0D2B52]">Check your agent&apos;s record</h3>
                <p className="text-[12.5px] text-[#6B7280] mt-1 leading-relaxed">
                  Search real estate, strata and stock-and-station agents the NSW regulator has actioned ,
                  disciplinary actions, prosecutions, licence cancellations, high-risk penalties.
                </p>
              </div>
              <button
                onClick={closeModal}
                className="btn-ghost p-1.5"
                data-testid="nsw-agent-check-close"
                aria-label="Close"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            {/* Search box */}
            <div className="px-5 py-4 border-b border-[#E5E4DE] bg-white">
              <form
                onSubmit={(e) => { e.preventDefault(); run(); }}
                className="flex gap-2"
              >
                <div className="flex-1 relative">
                  <Search size={13} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Agency name or licence number"
                    className="input w-full pl-9 text-sm"
                    data-testid="nsw-agent-check-input"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-teal text-sm inline-flex items-center gap-1.5"
                  data-testid="nsw-agent-check-submit"
                >
                  {busy ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Search size={13} strokeWidth={1.5} />}
                  Search
                </button>
              </form>
              {err && (
                <p className="text-[12px] text-[#B35D4C] mt-2 inline-flex items-center gap-1">
                  <AlertTriangle size={11} strokeWidth={1.5} /> {err}
                </p>
              )}
            </div>

            {/* Results */}
            <div className="px-5 py-4 space-y-3" data-testid="nsw-agent-check-results">
              {!result && !busy && (
                <p className="text-[13px] text-[#6B7280] italic py-6 text-center">
                  Type the agency or agent name above and hit Search.
                </p>
              )}
              {result && result.matches?.length === 0 && (
                <div className="bg-[#E6F5F2] border border-[#1B6C62]/30 p-4 text-[13px] text-[#0D2B52] flex items-start gap-2">
                  <CheckCircle2 size={14} strokeWidth={1.5} className="text-[#1B6C62] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">No matches found for &ldquo;{result.query}&rdquo;.</p>
                    <p className="text-[#6B7280] mt-1 text-[12px]">
                      This doesn&apos;t mean the agent has a clean record, it just means there&apos;s no published action under
                      this exact name. The register only covers <strong>NSW</strong> real estate / strata / stock &amp; station agents.
                      Try variations of the business name or licence number.
                    </p>
                  </div>
                </div>
              )}

              {result && result.matches?.length > 0 && (
                <>
                  <p className="text-[11.5px] font-mono text-[#8A6D3B] uppercase tracking-wider">
                    {result.total} {result.total === 1 ? "match" : "matches"} on the register
                  </p>
                  {result.matches.map((m, i) => (
                    <div
                      key={i}
                      className="bg-white border border-[#C9A961]/50 p-4 text-[13px]"
                      data-testid={`nsw-agent-check-row-${i}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="font-serif text-[15px] text-[#0D2B52] leading-tight">{m.party_served}</div>
                          <div className="label-mono text-[10.5px] mt-1">
                            {m.action}{m.date_of_action && ` · ${m.date_of_action}`}
                          </div>
                        </div>
                        {m.penalty_amount && m.penalty_amount !== "N/A" && (
                          <span className="font-mono text-[12px] bg-[#FAF6E8] text-[#8A6D3B] px-2 py-0.5 border border-[#C9A961]/40 whitespace-nowrap">
                            {m.penalty_amount}
                          </span>
                        )}
                      </div>
                      {m.description && (
                        <p className="text-[12.5px] text-[#1A1A1A] font-serif leading-relaxed mt-2">{m.description}</p>
                      )}
                      <div className="mt-2 pt-2 border-t border-[#E5E4DE] grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#6B7280] font-mono">
                        {m.licence_number && <div>Licence: <span className="text-[#0D2B52]">{m.licence_number}</span></div>}
                        {m.licence_type && m.licence_type !== "N/A" && <div className="truncate" title={m.licence_type}>Type: <span className="text-[#0D2B52]">{m.licence_type}</span></div>}
                        {m.abn_acn && m.abn_acn !== "N/A" && <div>ABN/ACN: <span className="text-[#0D2B52]">{m.abn_acn}</span></div>}
                        {m.suburb && <div className="truncate" title={m.suburb}>Location: <span className="text-[#0D2B52]">{m.suburb}</span></div>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-[#E5E4DE] bg-[#FAF9F6] text-[11px] text-[#6B7280] flex items-center justify-between gap-3">
              <span>
                Data: NSW Fair Trading{result?.cached && " (cached)"}.
                Compass is not affiliated with NSW Government.
              </span>
              <a
                href={result?.source_url || "https://www.nsw.gov.au/departments-and-agencies/fair-trading/how-we-regulate/name-and-shame-register#search-the-register"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#1B6C62] hover:underline flex-shrink-0"
                data-testid="nsw-agent-check-source-link"
              >
                Open on nsw.gov.au <ExternalLink size={10} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
